import Image from "next/image";
import Link from "next/link";
import { NextSeo } from "next-seo";
import { ReactElement } from "react";
import { BookOpen, CircleHelp, Scale } from "lucide-react";
import FaqComponent from "~/components/Faq/Faq";
import FaqSectionsData from "~/components/Faq/FaqContent";
import ServiceSelector from "~/components/ServiceSelector";
import SolutionSelector from "~/components/SolutionSelector";
import ServiceHighlight from "~/components/ServiceHighlight";
import SubscriptionServiceBanner from "~/components/SubscriptionServiceBanner";
import { MainLayout } from "~/components/layouts/MainLayout";
import { MarginLayout } from "~/components/layouts/MarginLayout";
import { NextPageWithLayout } from "~/pages/_app";
import SectionStrip from "~/components/SectionStrip";
import { HomeCitySection } from "~/components/HomeCitySection";
import { BG, TEXT } from "~/components/theme/colors";
import { HomePageContent as content } from "~/content/homePageContent";
import { ROUTES } from "~/content/Routes";
import heroInspektomatImage from "~/src/assets/images/hero-inspektomat.webp";
import homeElectricalImage from "~/src/assets/images/hero-power-box.webp";
import homeGasImage from "~/src/assets/images/gas_10.jpg";
import homeSmokeImage from "~/src/assets/images/smoke-alarm-check.webp";

const selectedId = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const inlineLinkClasses =
  "font-medium text-sky-700 underline underline-offset-2 transition-colors hover:text-sky-900";

const HomePage: NextPageWithLayout = () => {
  const electricalDescription = [
    content.electrical.paragraphs[0],
    <>
      Dlatego przez platformę zamówisz nie tylko{" "}
      <Link href={ROUTES.ELECTRICAL_CHECK} className={inlineLinkClasses}>
        obowiązkowy przegląd instalacji elektrycznej
      </Link>{" "}
      i{" "}
      <Link href={ROUTES.LIGHTNING_CHECK} className={inlineLinkClasses}>
        przegląd instalacji odgromowej
      </Link>
      , ale też{" "}
      <Link href={ROUTES.ELECTRICAL_MEASUREMENTS} className={inlineLinkClasses}>
        pomiary elektryczne
      </Link>
      ,{" "}
      <Link href={ROUTES.SWITCHBOARD_INSPECTION} className={inlineLinkClasses}>
        kontrolę rozdzielni
      </Link>
      ,{" "}
      <Link href={ROUTES.PV_INSTALLATION_INSPECTION} className={inlineLinkClasses}>
        przegląd instalacji fotowoltaicznej
      </Link>{" "}
      oraz{" "}
      <Link href={ROUTES.ENERGY_STORAGE_INSPECTION} className={inlineLinkClasses}>
        magazynu energii
      </Link>
      . To rozwiązanie dla mieszkań, domów, wspólnot, lokali usługowych i budynków
      firmowych.
    </>,
    content.electrical.paragraphs[2],
  ];

  const gasDescription = [
    content.gas.paragraphs[0],
    <>
      Na platformie znajdziesz wykonawców do{" "}
      <Link href={ROUTES.GAS_CHECK} className={inlineLinkClasses}>
        rocznych przeglądów instalacji gazowej
      </Link>
      ,{" "}
      <Link href={ROUTES.GAS_TIGHTNESS_TEST} className={inlineLinkClasses}>
        prób szczelności
      </Link>
      ,{" "}
      <Link href={ROUTES.GAS_BOILER} className={inlineLinkClasses}>
        serwisu kotłów gazowych
      </Link>{" "}
      oraz{" "}
      <Link href={ROUTES.GAS_INSTALLATION_ACCEPTANCE} className={inlineLinkClasses}>
        odbiorów nowych i modernizowanych instalacji
      </Link>
      . To usługi potrzebne zarówno przy bieżącej eksploatacji, jak i przed
      uruchomieniem instalacji po pracach montażowych.
    </>,
    content.gas.paragraphs[2],
  ];

  const otherDescription = [
    content.smoke.description[0],
    <>
      Dlatego na platformie znajdziesz zarówno wykonawców do{" "}
      <Link href={ROUTES.CHIMNEY} className={inlineLinkClasses}>
        przeglądu kominiarskiego
      </Link>
      ,{" "}
      <Link href={ROUTES.VENTILATION} className={inlineLinkClasses}>
        przeglądu wentylacji
      </Link>
      ,{" "}
      <Link href={ROUTES.AIR_CONDITIONING} className={inlineLinkClasses}>
        serwisu klimatyzacji
      </Link>{" "}
      i{" "}
      <Link href={ROUTES.HEAT_PUMP} className={inlineLinkClasses}>
        pomp ciepła
      </Link>
      , jak i firmy od{" "}
      <Link href={ROUTES.FIRE_DETECTION_INSPECTION} className={inlineLinkClasses}>
        systemów wykrywania pożaru
      </Link>{" "}
      oraz{" "}
      <Link href={ROUTES.BUILDING_INSPECTION} className={inlineLinkClasses}>
        przeglądów budowlanych
      </Link>
      .
    </>,
    content.smoke.description[2],
  ];

  return (
    <>
      <section className="relative flex min-h-[65vh] flex-col justify-center overflow-hidden bg-slate-950 pt-8">
        <div className="pointer-events-none absolute inset-0">
          <Image
            src={heroInspektomatImage}
            alt=""
            fill
            priority
            sizes="100vw"
            className="object-cover object-top"
          />
          <div className="absolute inset-0 bg-slate-950/50" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.72)_0%,rgba(2,6,23,0.28)_38%,rgba(248,250,252,0.96)_100%)]" />
        </div>
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-8 pt-4 sm:px-6 lg:px-8">
          <div className="w-full md:w-1/3">
            <ServiceSelector className="!max-w-[563px]" />
          </div>
        </div>
      </section>

      <SectionStrip
        title={content.titles.subTitle1}
        bgClassName={`${BG.primary} ${TEXT.onPrimary}`}
        className="!mt-0"
      />
      <MarginLayout>
        <SolutionSelector />
      </MarginLayout>

      <SectionStrip
        title="Lista firm z całej Polski"
        bgClassName={`${BG.primary} ${TEXT.onPrimary}`}
      />
      <HomeCitySection />

      <SectionStrip
        title={content.titles.subTitle2}
        bgClassName={`${BG.primary} ${TEXT.onPrimary}`}
        paddingClassName="px-4 py-12 sm:py-24 lg:px-8"
      />
      <MarginLayout className="mx-auto max-w-7xl">
        <ServiceHighlight
          title={content.electrical.title}
          description={electricalDescription}
          subTitle={content.electrical.subTitle}
          bullets={content.electrical.bullets}
          note={content.electrical.note}
          imageSrc={homeElectricalImage}
          imageAlt="Technik wykonujący pomiary rozdzielnicy elektrycznej"
          imageSizes="(min-width: 1024px) 33vw,(min-width: 640px) 60vw, 100vw"
        />

        <ServiceHighlight
          title={content.gas.title}
          description={gasDescription}
          subTitle={content.gas.subTitle}
          bullets={content.gas.bullets}
          note={content.gas.closing}
          imageSrc={homeGasImage}
          imageAlt="Specjalista kontrolujący instalację gazową w kuchni"
          imageSizes="(min-width: 1024px) 33vw,(min-width: 640px) 50vw, 100vw"
          reverseOnDesktop
        />

        <ServiceHighlight
          title={content.smoke.title}
          description={otherDescription}
          subTitle={content.smoke.subTitle}
          bullets={content.smoke.bullets}
          note={content.smoke.note}
          imageSrc={homeSmokeImage}
          imageAlt="Technik kontrolujący system bezpieczeństwa w budynku"
          imageSizes="(min-width: 1024px) 33vw,(min-width: 640px) 50vw, 100vw"
        />
      </MarginLayout>
      <SubscriptionServiceBanner />

      <SectionStrip
        title="Dowiedz się więcej o bezpieczeństwie"
        bgClassName={`${BG.primary} ${TEXT.onPrimary}`}
      />
      <MarginLayout>
        <SolutionSelector
          items={[
            { icon: BookOpen, label: "Baza Wiedzy", href: ROUTES.BLOG },
            { icon: Scale, label: "Legislacja", href: ROUTES.LEGISLATION },
            { icon: CircleHelp, label: "FAQ", href: ROUTES.FAQ },
          ]}
          gridClassName="grid-cols-3 max-w-xl mx-auto gap-4 mb-10"
        />
      </MarginLayout>

      <FaqComponent sections={FaqSectionsData} selectedIds={selectedId} />
    </>
  );
};

HomePage.getLayout = (page: ReactElement) => {
  return (
    <MainLayout>
      <NextSeo
        title={content.seo.title}
        description={content.seo.description}
      />
      {page}
    </MainLayout>
  );
};

export default HomePage;
